/**
 * UIModelStateContext for UI-Model-1 (API-Direct Approach)
 *
 * Core state management context using useReducer.
 * Uses API format directly - no normalization layer.
 *
 * Features:
 * - Direct API structure storage (UIModelState with model property)
 * - Command pattern integration with snapshot-based undo/redo
 * - Traversal-based getters via selectors
 * - Dirty tracking for save queue integration
 *
 * Line limit: ~300 lines
 */

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import {
  UIModelState,
  ContainerAPI,
  ItemAPI,
  GroupAPI,
  PageAPI,
  CanvasAPI,
  DirtySection,
  HistoryEntry,
  LookupResult,
  UsedFieldInfo,
  SubmodelPlacementInfo,
  isFieldAPI,
  isCategoryAPI,
  isConnectorAPI,
  WidgetAPI,
} from '../types/state.types';
import type {
  UIModelAPI,
  UsedComponentAPI,
  NestedContainerItemAPI,
} from '../types/api.types';
import {
  UIModelStateContextValue,
  UIModelStateAction,
} from '../types/context.types';
import { UndoableCommand } from '../types/command.types';
import { executeCommand, undoCommand } from '../commands/executor';
import * as selectors from '../selectors';

// ============================================================================
// Constants
// ============================================================================

const MAX_HISTORY_SIZE = 50;

// ============================================================================
// Field Tracking Maps Builder
// ============================================================================

/**
 * Generate a unique key for a field based on its origin.
 * For submodel fields: `${parentId}-${fieldName}`
 * For root fields: `${fieldName}`
 */
const getFieldKey = (fieldName: string, parentId?: string): string => {
  return parentId ? `${parentId}-${fieldName}` : fieldName;
};

/**
 * Generate a unique key for a category based on its origin.
 * For submodel categories: `${parentId}-${categoryId}`
 * For root categories: `${categoryId}`
 */
const getCategoryKey = (categoryId: string, parentId?: string): string => {
  return parentId ? `${parentId}-${categoryId}` : categoryId;
};

/**
 * Generate a unique key for a connector based on its origin.
 * For submodel connectors: `${parentId}-${connectorName}`
 * For root connectors: `${connectorName}`
 */
const getConnectorKey = (connectorName: string, parentId?: string): string => {
  return parentId ? `${parentId}-${connectorName}` : connectorName;
};

/**
 * Result of building used component maps
 */
interface UsedComponentMaps {
  usedFieldsMap: Map<string, UsedFieldInfo>;
  usedCategoriesMap: Map<string, UsedFieldInfo>;
  usedConnectorsMap: Map<string, UsedFieldInfo>;
  submodelPlacementMap: Map<string, SubmodelPlacementInfo>;
  containerToSubmodelMap: Map<string, string>;
}

/**
 * Build usedFieldsMap, usedCategoriesMap, and usedConnectorsMap from the model
 * by scanning all containers recursively.
 * This is an O(n) single-pass operation where n = total items across all containers.
 *
 * Tracks which fields, categories, and connectors are used in which containers (for global uniqueness)
 *
 * @param model - The UIModelAPI to scan
 * @returns Object containing all three maps with O(1) lookups
 */
const buildUsedComponentMaps = (model: UIModelAPI | null): UsedComponentMaps => {
  const usedFieldsMap = new Map<string, UsedFieldInfo>();
  const usedCategoriesMap = new Map<string, UsedFieldInfo>();
  const usedConnectorsMap = new Map<string, UsedFieldInfo>();
  const submodelPlacementMap = new Map<string, SubmodelPlacementInfo>();
  const containerToSubmodelMap = new Map<string, string>();

  if (!model?.layout?.canvas) {
    return { usedFieldsMap, usedCategoriesMap, usedConnectorsMap, submodelPlacementMap, containerToSubmodelMap };
  }

  // Build canvasId → pageName lookup from navigation
  const canvasToPageName = new Map<string, string>();
  (model.layout?.navigation?.groups || []).forEach(group => {
    (group.pages || []).forEach(page => {
      if (page.canvasId) {
        canvasToPageName.set(page.canvasId, page.name);
      }
    });
  });

  const scanContainer = (
    container: ContainerAPI,
    pageName: string | undefined,
    canvasId: string
  ) => {
    // Track submodel placement (Rule 1 & 2)
    if (container.content?.submodel?.id) {
      const smId = container.content.submodel.id;
      containerToSubmodelMap.set(container.id, smId);
      // Only record the first occurrence per submodel (first container wins)
      if (!submodelPlacementMap.has(smId)) {
        submodelPlacementMap.set(smId, {
          submodelId: smId,
          containerId: container.id,
          containerName: container.name,
          canvasId,
          pageName,
        });
      }
    }

    // Scan direct items for fields, categories, and connectors
    (container.content?.items || []).forEach(item => {
      // Track fields
      if (isFieldAPI(item)) {
        const fieldKey = getFieldKey(item.name, item.parentId);
        usedFieldsMap.set(fieldKey, {
          containerId: container.id,
          containerName: container.name,
          itemId: item.id,
          parentId: item.parentId,
          pageName,
        });
      }

      // Track categories
      if (isCategoryAPI(item)) {
        const categoryKey = getCategoryKey(item.categoryId, item.parentId);
        usedCategoriesMap.set(categoryKey, {
          containerId: container.id,
          containerName: container.name,
          itemId: item.id,
          parentId: item.parentId,
          pageName,
        });
      }

      // Track connectors
      if (isConnectorAPI(item)) {
        const connectorKey = getConnectorKey(item.name, item.parentId);
        usedConnectorsMap.set(connectorKey, {
          containerId: container.id,
          containerName: container.name,
          itemId: item.id,
          parentId: item.parentId,
          pageName,
        });
      }
    });

    // Recursively scan nested containers
    (container.content?.nestedContainers || []).forEach(nested =>
      scanContainer(nested, pageName, canvasId)
    );
  };

  // Scan all canvases with their page names
  Object.entries(model.layout.canvas).forEach(([canvasId, canvas]) => {
    const pageName = canvasToPageName.get(canvasId);
    // Skip orphan canvases (no corresponding page in navigation)
    // These are stale entries from old module or failed deletions
    if (!pageName) return;
    (canvas.containers || []).forEach(container =>
      scanContainer(container, pageName, canvasId)
    );
  });

  return { usedFieldsMap, usedCategoriesMap, usedConnectorsMap, submodelPlacementMap, containerToSubmodelMap };
};

// ============================================================================
// UsageInfo Synchronization (Bi-Directional API Compatibility)
// ============================================================================

/**
 * Build usageInfo.usedComponents for a single container from its content.
 * This ensures container-level usageInfo stays in sync for API compatibility.
 *
 * @param container - The container to build usageInfo for
 * @returns Array of UsedComponentAPI entries
 */
const buildContainerUsageInfo = (container: ContainerAPI): UsedComponentAPI[] => {
  const usedComponents: UsedComponentAPI[] = [];

  // Add submodel if present
  if (container.content?.submodel?.id) {
    const submodel = container.content.submodel;
    usedComponents.push({
      id: `${submodel.id}-instance-${container.id}`,
      type: 'submodel',
      originalComponentId: submodel.id,
      isReusable: false, // Submodels are not reusable
    });
  }

  // Add items (fields, widgets, categories, connectors)
  for (const item of container.content?.items || []) {
    // Skip NestedContainerItemAPI entries (they have nestedContainers but no type)
    if ('nestedContainers' in item && !('type' in item)) {
      continue;
    }

    // Type guard: ensure item has type property (is ItemAPI)
    if (!('type' in item)) {
      continue;
    }

    const typedItem = item as ItemAPI;
    // For widgets, use widgetType as stable originalComponentId (e.g. "yourBuild")
    // instead of instance UUID, so the preview app can resolve widget type definitions.
    const originalId = typedItem.type === 'widget' && 'widgetType' in typedItem
      ? (typedItem as WidgetAPI).widgetType || typedItem.id
      : typedItem.id;
    const usedComponent: UsedComponentAPI = {
      id: typedItem.id,
      type: typedItem.type,
      originalComponentId: originalId,
      isReusable:
        typedItem.isReusable ??
        (typedItem.type === 'field' ||
          typedItem.type === 'category' ||
          typedItem.type === 'connector'),
    };

    // Add parentId for submodel-scoped items
    if (typedItem.parentId) {
      usedComponent.parentId = typedItem.parentId;
    }

    // Add content for content block widgets (prefer root-level, fallback to configuration)
    if (
      typedItem.type === 'widget' &&
      'widgetType' in typedItem &&
      typedItem.widgetType === 'contentBlock'
    ) {
      usedComponent.content =
        (typedItem as any).content ||
        (typedItem.configuration?.content as string) ||
        '';
    }

    usedComponents.push(usedComponent);
  }

  return usedComponents;
};

/**
 * Synchronize usageInfo for all containers in the model.
 * Modifies containers in-place for efficiency.
 *
 * Nested containers can appear in TWO places:
 * 1. content.nestedContainers - direct property on ContainerContentAPI
 * 2. As items in content.items[] via NestedContainerItemAPI (has nestedContainers but no type)
 *
 * @param model - The UIModelAPI to synchronize
 */
const syncAllContainerUsageInfo = (model: UIModelAPI): void => {
  if (!model?.layout?.canvas) return;

  const processContainer = (container: ContainerAPI) => {
    // Rebuild usageInfo from content
    container.usageInfo = {
      usedComponents: buildContainerUsageInfo(container),
    };

    // Process nested containers from content.nestedContainers
    if (container.content?.nestedContainers) {
      for (const nested of container.content.nestedContainers) {
        processContainer(nested);
      }
    }

    // Also check items array for NestedContainerItemAPI entries
    // (items with nestedContainers property but no type)
    for (const item of container.content?.items || []) {
      if ('nestedContainers' in item && !('type' in item)) {
        const nestedItem = item as NestedContainerItemAPI;
        for (const nested of nestedItem.nestedContainers) {
          processContainer(nested);
        }
      }
    }
  };

  // Process all canvases and their containers
  for (const canvasId of Object.keys(model.layout.canvas)) {
    const canvas = model.layout.canvas[canvasId];
    for (const container of canvas.containers || []) {
      processContainer(container);
    }
  }
};

// ============================================================================
// Shared Reducer Helpers
// ============================================================================

/**
 * Normalize a single container: ensure metadata and rowIndex exist.
 * Mutates in-place for efficiency (called on fresh API data).
 */
const normalizeContainer = (container: ContainerAPI, isNested: boolean): void => {
  if (!container.metadata) {
    container.metadata = {
      type: isNested ? 'nested-container' : 'container',
      isNested,
      rowIndex: container.rowIndex ?? 0,
    };
  }
  if (container.rowIndex === undefined) {
    container.rowIndex = container.metadata.rowIndex ?? 0;
  }
  for (const nested of container.content?.nestedContainers || []) {
    normalizeContainer(nested, true);
  }
};

/**
 * Normalize all containers in a model to ensure they have metadata and rowIndex.
 * Called during SET_MODEL so server-created containers match the expected format.
 */
const normalizeContainers = (model: UIModelAPI): void => {
  if (!model?.layout?.canvas) return;
  for (const canvasId of Object.keys(model.layout.canvas)) {
    const canvas = model.layout.canvas[canvasId];
    for (const container of canvas.containers || []) {
      normalizeContainer(container, false);
    }
  }
};

/**
 * Rebuild all used-component maps from a model AND sync container-level usageInfo.
 * Used after any operation that modifies items (SET_MODEL, EXECUTE_COMMAND, UNDO, REDO, APPLY_SYNC).
 */
const rebuildMapsAndSync = (model: UIModelAPI | null): UsedComponentMaps => {
  const maps = buildUsedComponentMaps(model);
  if (model) {
    syncAllContainerUsageInfo(model);
  }
  return maps;
};

/**
 * Restore a history snapshot (shared logic for UNDO and REDO).
 * Rebuilds maps, syncs usage info, and returns the new state with updated history.
 */
const restoreSnapshot = (
  state: UIModelState,
  snapshot: UIModelAPI | null,
  newPast: HistoryEntry[],
  newFuture: HistoryEntry[]
): UIModelState => {
  const maps = rebuildMapsAndSync(snapshot);
  return {
    ...state,
    model: snapshot,
    ...maps,
    history: { ...state.history, past: newPast, future: newFuture },
  };
};

/**
 * Update dirty tracker by adding or deleting a section/canvas (shared for MARK_DIRTY / MARK_CLEAN).
 */
const updateDirtyTracker = (
  state: UIModelState,
  section: DirtySection,
  canvasId: string | undefined,
  op: 'add' | 'delete'
): UIModelState => {
  const newSections = new Set(Array.from(state.dirtyTracker.sections));
  newSections[op](section);

  const newModifiedCanvases = new Set(Array.from(state.dirtyTracker.canvasPages));
  if (canvasId) {
    newModifiedCanvases[op](canvasId);
  }

  return {
    ...state,
    dirtyTracker: { ...state.dirtyTracker, sections: newSections, canvasPages: newModifiedCanvases },
  };
};

// ============================================================================
// Initial State
// ============================================================================

const createInitialState = (): UIModelState => ({
  model: null,
  editorState: {
    activeGroupId: null,
    activePageId: null,
    selectedContainerId: null,
    selectedItemId: null,
    selectedItemType: null,
    selectedSubmodelId: null,
    selectedSection: null,
    selectionPath: [],
    selectedGroupId: null,
    selectedPageId: null,
    selectedPageGroupId: null,
  },
  history: {
    past: [],
    future: [],
    maxSize: MAX_HISTORY_SIZE,
  },
  dirtyTracker: {
    sections: new Set(),
    canvasPages: new Set(),
    lastSaveTimestamp: {
      header: 0,
      footer: 0,
      navigation: 0,
      canvas: 0,
      theme: 0,
      customCss: 0,
      sharedWidgets: 0,
    },
    pendingSaveCount: 0,
  },
  usedFieldsMap: new Map(),
  usedCategoriesMap: new Map(),
  usedConnectorsMap: new Map(),
  submodelPlacementMap: new Map(),
  containerToSubmodelMap: new Map(),
  isLoading: false,
  isSaving: false,
  error: null,
  lastSaveError: null,
});

// ============================================================================
// Reducer
// ============================================================================

const uiModelReducer = (
  state: UIModelState,
  action: UIModelStateAction
): UIModelState => {
  switch (action.type) {
    case 'SET_MODEL': {
      const model = action.payload;

      // Normalize containers: ensure metadata and rowIndex exist on all containers
      if (model?.layout?.canvas) {
        normalizeContainers(model);
      }

      // Extract editorState from API response if available
      const apiEditorState = model?.editorState;

      // Build used component maps and sync usageInfo
      const maps = rebuildMapsAndSync(model);

      // Initial load: state.model is null, use API editorState or defaults
      // Update: state.model exists, preserve current editorState (selections)
      const isInitialLoad = state.model === null;

      if (isInitialLoad) {
        // Initial load - reset to fresh state with API values
        const initialState = createInitialState();

        // Validate API editor state IDs exist in model before using them
        const groups = model?.layout?.navigation?.groups || [];
        const sortedGroups = [...groups].sort((a, b) => a.order - b.order);

        // Validate API group ID exists; fall back to first group
        const apiGroupValid = apiEditorState?.activeGroupId
          && groups.some(g => g.id === apiEditorState.activeGroupId);
        const resolvedGroupId = apiGroupValid
          ? apiEditorState!.activeGroupId
          : (sortedGroups.length > 0 ? sortedGroups[0].id : null);

        // Get pages for the resolved group
        const resolvedGroup = groups.find(g => g.id === resolvedGroupId);
        const sortedPages = resolvedGroup
          ? [...(resolvedGroup.pages || [])].sort((a, b) => a.order - b.order)
          : [];

        // Validate API page ID exists in the resolved group; fall back to first page
        const apiPageValid = apiEditorState?.activePageId
          && sortedPages.some(p => p.id === apiEditorState.activePageId);
        const resolvedPageId = apiPageValid
          ? apiEditorState!.activePageId
          : (sortedPages.length > 0 ? sortedPages[0].id : null);

        return {
          ...initialState,
          model,
          editorState: {
            ...initialState.editorState,
            activeGroupId: resolvedGroupId,
            activePageId: resolvedPageId,
            selectedContainerId: apiEditorState?.selectedContainerId ?? null,
          },
          ...maps,
          isLoading: false,
          error: null,
        };
      }

      // Model update - preserve existing editorState (selections)
      return {
        ...state,
        model,
        ...maps,
        isLoading: false,
        error: null,
      };
    }

    case 'EXECUTE_COMMAND': {
      const command = action.payload;

      // Create snapshot for history (snapshot-based undo)
      const historyEntry: HistoryEntry = {
        timestamp: Date.now(),
        description: command.description,
        snapshot: state.model ? JSON.parse(JSON.stringify(state.model)) : null,
      };

      // Execute the command
      const newState = executeCommand(state, command);

      // Rebuild maps and sync usageInfo after command execution
      const maps = rebuildMapsAndSync(newState.model);

      // Add to history, clear future (new action invalidates redo)
      return {
        ...newState,
        ...maps,
        history: {
          ...newState.history,
          past: [...state.history.past, historyEntry].slice(-MAX_HISTORY_SIZE),
          future: [],
        },
      };
    }

    case 'UNDO': {
      if (state.history.past.length === 0) return state;

      const previousEntry = state.history.past[state.history.past.length - 1];
      const currentEntry: HistoryEntry = {
        timestamp: Date.now(),
        description: 'Current state',
        snapshot: state.model ? JSON.parse(JSON.stringify(state.model)) : null,
      };

      return restoreSnapshot(
        state,
        previousEntry.snapshot,
        state.history.past.slice(0, -1),
        [currentEntry, ...state.history.future]
      );
    }

    case 'REDO': {
      if (state.history.future.length === 0) return state;

      const nextEntry = state.history.future[0];
      const currentEntry: HistoryEntry = {
        timestamp: Date.now(),
        description: 'Current state',
        snapshot: state.model ? JSON.parse(JSON.stringify(state.model)) : null,
      };

      return restoreSnapshot(
        state,
        nextEntry.snapshot,
        [...state.history.past, currentEntry],
        state.history.future.slice(1)
      );
    }

    case 'CLEAR_HISTORY':
      return {
        ...state,
        history: {
          past: [],
          future: [],
          maxSize: state.history.maxSize,
        },
      };

    case 'MARK_DIRTY':
      return updateDirtyTracker(state, action.payload.section, action.payload.canvasId, 'add');

    case 'MARK_CLEAN':
      return updateDirtyTracker(state, action.payload.section, action.payload.canvasId, 'delete');

    case 'RESET_DIRTY_TRACKER':
      return {
        ...state,
        dirtyTracker: {
          sections: new Set(),
          canvasPages: new Set(),
          lastSaveTimestamp: {
            header: 0,
            footer: 0,
            navigation: 0,
            canvas: 0,
            theme: 0,
            customCss: 0,
            sharedWidgets: 0,
          },
          pendingSaveCount: 0,
        },
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_SAVING':
      return {
        ...state,
        isSaving: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'RESET_STATE':
      return createInitialState();

    case 'SET_ACTIVE_GROUP':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          activeGroupId: action.payload.groupId,
        },
      };

    case 'SET_ACTIVE_PAGE':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          activePageId: action.payload.pageId,
        },
      };

    case 'SET_SELECTED_CONTAINER': {
      const containerId = action.payload.containerId;
      return {
        ...state,
        editorState: {
          ...state.editorState,
          selectedContainerId: containerId,
          // Clear item/submodel selection when selecting a container
          selectedItemId: null,
          selectedItemType: null,
          selectedSubmodelId: null,
          // Only clear section/group/page selection when actually selecting a container (not just clearing)
          // This preserves page/group selection when they dispatch SET_SELECTED_CONTAINER with null
          ...(containerId !== null && {
            selectedSection: null,
            selectedGroupId: null,
            selectedPageId: null,
            selectedPageGroupId: null,
          }),
        },
      };
    }

    case 'SET_SELECTED_ITEM':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          selectedItemId: action.payload.itemId,
          selectedItemType: action.payload.itemType,
          selectedContainerId: action.payload.containerId,
          selectedSubmodelId: null,
          selectedSection: null,
          selectedGroupId: null,
          selectedPageId: null,
          selectedPageGroupId: null,
        },
      };

    case 'SET_SELECTED_SECTION':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          selectedSection: action.payload.section,
          // Clear container/item selection when selecting a section
          selectedContainerId: null,
          selectedItemId: null,
          selectedItemType: null,
          selectedSubmodelId: null,
          // Clear group/page selection when selecting a section
          selectedGroupId: null,
          selectedPageId: null,
          selectedPageGroupId: null,
        },
      };

    case 'SET_SELECTED_SUBMODEL':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          selectedSubmodelId: action.payload.submodelId,
          selectedContainerId: action.payload.containerId,
          // Clear other selections
          selectedItemId: null,
          selectedItemType: null,
          selectedSection: null,
          selectedGroupId: null,
          selectedPageId: null,
          selectedPageGroupId: null,
        },
      };

    case 'SET_SELECTED_GROUP':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          selectedGroupId: action.payload.groupId,
          // Clear other selections when selecting a group
          selectedSection: null,
          selectedContainerId: null,
          selectedItemId: null,
          selectedItemType: null,
          selectedSubmodelId: null,
          selectedPageId: null,
          selectedPageGroupId: null,
        },
      };

    case 'SET_SELECTED_PAGE':
      return {
        ...state,
        editorState: {
          ...state.editorState,
          selectedPageId: action.payload.pageId,
          selectedPageGroupId: action.payload.groupId,
          // Clear other selections when selecting a page
          selectedSection: null,
          selectedContainerId: null,
          selectedItemId: null,
          selectedItemType: null,
          selectedSubmodelId: null,
          selectedGroupId: null,
        },
      };

    case 'UPDATE_THEME':
      return state.model
        ? {
            ...state,
            model: {
              ...state.model,
              theme: action.payload.theme,
            },
          }
        : state;

    case 'UPDATE_CUSTOM_CSS':
      return state.model
        ? {
            ...state,
            model: {
              ...state.model,
              customCss: {
                ...((state.model.customCss as Record<string, unknown>) || {}),
                globalStyles: action.payload.css,
              },
            },
          }
        : state;

    case 'APPLY_SYNC': {
      const syncedModel = action.payload.model;
      const affectedCanvasIds = action.payload.affectedCanvasIds;

      // Rebuild all lookup maps and sync usageInfo
      const syncMaps = rebuildMapsAndSync(syncedModel);

      // Mark affected canvases dirty so CanvasAutoSave picks them up
      const newSections = new Set(Array.from(state.dirtyTracker.sections));
      const newCanvasPages = new Set(Array.from(state.dirtyTracker.canvasPages));
      if (affectedCanvasIds.length > 0) {
        newSections.add('canvas' as DirtySection);
        for (const cid of affectedCanvasIds) {
          newCanvasPages.add(cid);
        }
      }

      // No history push — sync reflects external reality, not a user canvas action
      return {
        ...state,
        model: syncedModel,
        ...syncMaps,
        dirtyTracker: {
          ...state.dirtyTracker,
          sections: newSections,
          canvasPages: newCanvasPages,
        },
      };
    }

    default:
      return state;
  }
};

// ============================================================================
// Context
// ============================================================================

const UIModelStateContext = createContext<UIModelStateContextValue | null>(null);

interface UIModelStateProviderProps {
  children: ReactNode;
  initialModel?: UIModelState['model'];
  /**
   * Full (or partial) initial state override — primarily for tests that need
   * to hydrate history/dirtyTracker/editorState/etc. up front rather than via
   * dispatched actions. Applied on top of `initialModel`, so if both are
   * supplied, `initialState.model` (if present) wins.
   */
  initialState?: Partial<UIModelState>;
}

export const UIModelStateProvider = ({
  children,
  initialModel,
  initialState,
}: UIModelStateProviderProps) => {
  const [state, dispatch] = useReducer(uiModelReducer, undefined, () => {
    const initial = createInitialState();
    const withModel = initialModel ? { ...initial, model: initialModel } : initial;
    return initialState ? { ...withModel, ...initialState } : withModel;
  });

  // ============================================================================
  // Traversal-Based Getters (via selectors)
  // ============================================================================

  const getContainer = useCallback(
    (id: string): LookupResult<ContainerAPI> => {
      return selectors.selectContainerById(state, id);
    },
    [state]
  );

  const getItem = useCallback(
    (id: string): LookupResult<ItemAPI> => {
      return selectors.selectItemById(state, id);
    },
    [state]
  );

  const getGroup = useCallback(
    (id: string): GroupAPI | undefined => {
      return selectors.selectGroupById(state, id);
    },
    [state]
  );

  const getPage = useCallback(
    (id: string): PageAPI | undefined => {
      return selectors.selectPageById(state, id);
    },
    [state]
  );

  const getCanvas = useCallback(
    (canvasId: string): CanvasAPI | undefined => {
      return selectors.selectCanvas(state, canvasId);
    },
    [state]
  );

  // ============================================================================
  // Relationship Query Methods
  // ============================================================================

  const getContainersForCanvas = useCallback(
    (canvasId: string): ContainerAPI[] => {
      return selectors.selectContainersForCanvas(state, canvasId);
    },
    [state]
  );

  const getItemsForContainer = useCallback(
    (containerId: string): ItemAPI[] => {
      return selectors.selectItemsForContainer(state, containerId);
    },
    [state]
  );

  const getChildContainers = useCallback(
    (containerId: string): ContainerAPI[] => {
      return selectors.selectChildContainers(state, containerId);
    },
    [state]
  );

  const getPagesForGroup = useCallback(
    (groupId: string): PageAPI[] => {
      return selectors.selectPagesForGroup(state, groupId);
    },
    [state]
  );

  const getContainerPath = useCallback(
    (containerId: string): ContainerAPI[] => {
      return selectors.selectContainerPath(state, containerId);
    },
    [state]
  );

  // ============================================================================
  // Field Usage Tracking
  // ============================================================================

  /**
   * Check if a field is already used in the model (O(1) lookup).
   * Implements STRICT UNIQUENESS: each field can only exist in ONE container.
   *
   * Rules:
   * - Not used anywhere: AVAILABLE
   * - Used in ANY container: BLOCKED (strict uniqueness - disabled globally)
   *
   * @param fieldName - The field name
   * @param parentId - The parent submodel ID (for submodel fields)
   * @returns true if the field is blocked/unavailable, false if available
   */
  const isFieldUsed = useCallback(
    (fieldName: string, parentId?: string): boolean => {
      const fieldKey = parentId ? `${parentId}-${fieldName}` : fieldName;
      return state.usedFieldsMap.has(fieldKey);
    },
    [state.usedFieldsMap]
  );

  /**
   * Get usage information for a field (O(1) lookup)
   * @param fieldName - The field name
   * @param parentId - The parent submodel ID (for submodel fields)
   */
  const getFieldUsageInfo = useCallback(
    (fieldName: string, parentId?: string): UsedFieldInfo | undefined => {
      const fieldKey = parentId ? `${parentId}-${fieldName}` : fieldName;
      return state.usedFieldsMap.get(fieldKey);
    },
    [state.usedFieldsMap]
  );

  /**
   * Check if a category is already used in the model (O(1) lookup).
   * Implements STRICT UNIQUENESS: each category can only exist in ONE container.
   *
   * Rules:
   * - Not used anywhere: AVAILABLE
   * - Used in ANY container: BLOCKED (strict uniqueness - disabled globally)
   *
   * @param categoryId - The category ID
   * @param parentId - The parent submodel ID (for submodel categories)
   * @returns true if the category is blocked/unavailable, false if available
   */
  const isCategoryUsed = useCallback(
    (categoryId: string, parentId?: string): boolean => {
      const categoryKey = getCategoryKey(categoryId, parentId);
      return state.usedCategoriesMap.has(categoryKey);
    },
    [state.usedCategoriesMap]
  );

  /**
   * Get usage information for a category (O(1) lookup)
   * @param categoryId - The category ID
   * @param parentId - The parent submodel ID (for submodel categories)
   */
  const getCategoryUsageInfo = useCallback(
    (categoryId: string, parentId?: string): UsedFieldInfo | undefined => {
      const categoryKey = getCategoryKey(categoryId, parentId);
      return state.usedCategoriesMap.get(categoryKey);
    },
    [state.usedCategoriesMap]
  );

  /**
   * Check if a connector is already used in the model (O(1) lookup).
   * Implements STRICT UNIQUENESS: each connector can only exist in ONE container.
   *
   * Rules:
   * - Not used anywhere: AVAILABLE
   * - Used in ANY container: BLOCKED (strict uniqueness - disabled globally)
   *
   * @param name - The connector name
   * @param parentId - The parent submodel ID (for submodel connectors)
   * @returns true if the connector is blocked/unavailable, false if available
   */
  const isConnectorUsed = useCallback(
    (name: string, parentId?: string): boolean => {
      const connectorKey = getConnectorKey(name, parentId);
      return state.usedConnectorsMap.has(connectorKey);
    },
    [state.usedConnectorsMap]
  );

  /**
   * Get usage information for a connector (O(1) lookup)
   * @param name - The connector name
   * @param parentId - The parent submodel ID (for submodel connectors)
   */
  const getConnectorUsageInfo = useCallback(
    (name: string, parentId?: string): UsedFieldInfo | undefined => {
      const connectorKey = getConnectorKey(name, parentId);
      return state.usedConnectorsMap.get(connectorKey);
    },
    [state.usedConnectorsMap]
  );

  /**
   * Check if a submodel has ANY fields in containers that would BLOCK adding to the current container.
   * Used to disable entire submodel sections when selecting a different container.
   * Only blocks if a field is in a container OUTSIDE the current root hierarchy.
   * Containers within the same root tree (parent, siblings, children) share submodel access.
   *
   * @param submodelId - The submodel ID to check
   * @param currentContainerId - The currently selected container ID
   */
  const isSubmodelUsedElsewhere = useCallback(
    (submodelId: string, currentContainerId: string | null): boolean => {
      if (!currentContainerId) return false;

      // Get all containers in the same root hierarchy — siblings, parent, children
      const sameHierarchyIds = selectors.selectContainerIdsInRootHierarchy(state, currentContainerId);

      // Check all three usage maps — a submodel can have fields, categories, or connectors
      const allUsages = [
        ...Array.from(state.usedFieldsMap.values()),
        ...Array.from(state.usedCategoriesMap.values()),
        ...Array.from(state.usedConnectorsMap.values()),
      ];
      for (let i = 0; i < allUsages.length; i++) {
        const usage = allUsages[i];
        // Only block if the item is in a container OUTSIDE the current root hierarchy
        if (usage.parentId === submodelId && !sameHierarchyIds.has(usage.containerId)) {
          return true;
        }
      }
      return false;
    },
    [state.usedFieldsMap, state.usedCategoriesMap, state.usedConnectorsMap, state]
  );

  /**
   * Get the container name where a submodel's fields are used (for tooltip).
   * Only returns a container name when the submodel is actually BLOCKED for the current container.
   * Returns formatted string with page name: "Page X's Container Y" or just "Container Y" if no page.
   * Only reports usage from containers OUTSIDE the current root hierarchy.
   *
   * @param submodelId - The submodel ID to check
   * @param currentContainerId - The currently selected container ID
   */
  const getSubmodelUsageContainer = useCallback(
    (submodelId: string, currentContainerId: string | null): string | null => {
      if (!currentContainerId) return null;

      // Get all containers in the same root hierarchy
      const sameHierarchyIds = selectors.selectContainerIdsInRootHierarchy(state, currentContainerId);

      // Check all three usage maps — a submodel can have fields, categories, or connectors
      const allUsages = [
        ...Array.from(state.usedFieldsMap.values()),
        ...Array.from(state.usedCategoriesMap.values()),
        ...Array.from(state.usedConnectorsMap.values()),
      ];
      for (let i = 0; i < allUsages.length; i++) {
        const usage = allUsages[i];
        // Only return usage info if the item is OUTSIDE the current root hierarchy
        if (usage.parentId === submodelId && !sameHierarchyIds.has(usage.containerId)) {
          return usage.pageName
            ? `${usage.pageName}'s ${usage.containerName}`
            : usage.containerName;
        }
      }
      return null;
    },
    [state.usedFieldsMap, state.usedCategoriesMap, state.usedConnectorsMap, state]
  );

  // ============================================================================
  // Submodel Restriction Rules (Rule 1, 2, 3)
  // ============================================================================

  /**
   * Rule 1: One submodel per container.
   * Returns the effective submodel ID for a container (walks up parent hierarchy).
   * Uses selectEffectiveSubmodelId which checks the container and all ancestors.
   */
  const getContainerSubmodelId = useCallback(
    (containerId: string): string | null => {
      return selectors.selectEffectiveSubmodelId(state, containerId);
    },
    [state]
  );

  /**
   * Rule 2: Submodel page-locking.
   * If a submodel is placed on a different canvas than currentCanvasId,
   * returns the page name (for tooltip). Otherwise returns null (available).
   */
  const getSubmodelPageLock = useCallback(
    (submodelId: string, currentCanvasId: string | null): string | null => {
      const placement = state.submodelPlacementMap.get(submodelId);
      if (!placement) return null; // Not placed yet — available
      if (!currentCanvasId) return null; // No active canvas — can't determine lock
      if (placement.canvasId === currentCanvasId) return null; // Same page — available
      return placement.pageName || 'another page';
    },
    [state.submodelPlacementMap]
  );

  /**
   * Rule 3: Same root hierarchy check.
   * Returns true if both containers share the same root container.
   */
  const areContainersInSameRootHierarchy = useCallback(
    (containerIdA: string, containerIdB: string): boolean => {
      const rootA = selectors.selectRootContainer(state, containerIdA);
      const rootB = selectors.selectRootContainer(state, containerIdB);
      if (!rootA || !rootB) return false;
      return rootA.id === rootB.id;
    },
    [state]
  );

  // ============================================================================
  // Derived State
  // ============================================================================

  const canUndo = state.history.past.length > 0;
  const canRedo = state.history.future.length > 0;
  const isDirty = state.dirtyTracker.sections.size > 0 || state.dirtyTracker.canvasPages.size > 0;

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<UIModelStateContextValue>(
    () => ({
      state,
      dispatch,
      // Getters
      getContainer,
      getItem,
      getGroup,
      getPage,
      getCanvas,
      // Relationship queries
      getContainersForCanvas,
      getItemsForContainer,
      getChildContainers,
      getPagesForGroup,
      getContainerPath,
      // Field usage tracking
      isFieldUsed,
      getFieldUsageInfo,
      // Category usage tracking
      isCategoryUsed,
      getCategoryUsageInfo,
      // Connector usage tracking
      isConnectorUsed,
      getConnectorUsageInfo,
      // Submodel usage tracking
      isSubmodelUsedElsewhere,
      getSubmodelUsageContainer,
      // Submodel restriction rules
      getContainerSubmodelId,
      getSubmodelPageLock,
      areContainersInSameRootHierarchy,
      // State checks
      canUndo,
      canRedo,
      isDirty,
    }),
    [
      state,
      getContainer,
      getItem,
      getGroup,
      getPage,
      getCanvas,
      getContainersForCanvas,
      getItemsForContainer,
      getChildContainers,
      getPagesForGroup,
      getContainerPath,
      isFieldUsed,
      getFieldUsageInfo,
      isCategoryUsed,
      getCategoryUsageInfo,
      isConnectorUsed,
      getConnectorUsageInfo,
      isSubmodelUsedElsewhere,
      getSubmodelUsageContainer,
      getContainerSubmodelId,
      getSubmodelPageLock,
      areContainersInSameRootHierarchy,
      canUndo,
      canRedo,
      isDirty,
    ]
  );

  return (
    <UIModelStateContext.Provider value={value}>
      {children}
    </UIModelStateContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useUIModelState = (): UIModelStateContextValue => {
  const context = useContext(UIModelStateContext);
  if (!context) {
    throw new Error('useUIModelState must be used within a UIModelStateProvider');
  }
  return context;
};

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook to get just the state (for performance - avoids re-renders from dispatch changes)
 */
export const useUIModelStateValue = (): UIModelState => {
  const { state } = useUIModelState();
  return state;
};

/**
 * Hook for undo/redo operations
 */
export const useUndoRedo = () => {
  const { state, dispatch, canUndo, canRedo } = useUIModelState();

  const undo = useCallback(() => {
    dispatch({ type: 'UNDO' });
  }, [dispatch]);

  const redo = useCallback(() => {
    dispatch({ type: 'REDO' });
  }, [dispatch]);

  const undoDescription = selectors.selectUndoDescription(state);
  const redoDescription = selectors.selectRedoDescription(state);
  const historyLength = selectors.selectHistoryLength(state);

  return {
    undo,
    redo,
    canUndo,
    canRedo,
    undoDescription,
    redoDescription,
    historyLength,
  };
};

/**
 * Hook for executing commands
 */
export const useCommandExecutor = () => {
  const { dispatch } = useUIModelState();

  const execute = useCallback(
    (command: UndoableCommand) => {
      dispatch({ type: 'EXECUTE_COMMAND', payload: command });
    },
    [dispatch]
  );

  return execute;
};

/**
 * Generate a unique key for a field based on its origin.
 * Exported for use by ComponentLibrary when checking field availability.
 *
 * @param fieldName - The field name
 * @param parentId - The parent submodel ID (optional)
 * @returns The field key for usedFieldsMap lookup
 */
export const getFieldKeyForLookup = (fieldName: string, parentId?: string): string => {
  return parentId ? `${parentId}-${fieldName}` : fieldName;
};

export default UIModelStateContext;
